// HomeSections.jsx — v8
// Mobile responsive — useIsMobile hook added, all 2-col grids stack on <768px
// Contrast table collapses to RevUpAI-only cards on mobile
// All colors, copy, brand tokens preserved exactly from v7
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WRAP, SECTION_PAD } from '../styles/layout';

/* ─── Brand tokens ─────────────────────────────────────────────────────────── */
const C    = '#7C3AED';
const R    = '124,58,237';
const C2   = '#00D4AA';
const R2   = '0,212,170';
const BG   = '#09060F';
const LAV  = '#EDE9F8';
const LAV2 = '#C8C0EC';
const LAV3 = '#9B90BA';
const PAD  = 'clamp(24px,4vw,72px)';

/* ─── Mobile hook ───────────────────────────────────────────────────────────── */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

/* ─── Role / product data ───────────────────────────────────────────────────── */
const ROLES = {
  revsec: {
    id: 'revsec', name: 'RevSec', label: 'AI CISO',
    color: '#7C3AED', rgb: '124,58,237',
    description: "Your AI CISO that never sleeps. RevSec gives your security team complete visibility into every AI tool, agent, and MCP server in your organisation — with autonomous policy enforcement, real-time threat detection, and board-ready compliance reporting. From zero visibility to fully governed in under 48 hours.",
    audience: "For CISOs, Security Operations, and GRC teams managing AI risk at scale",
    stats: [
      { value: '89%',   label: 'shadow AI found within 48 hours' },
      { value: '$670K', label: 'avg cost of a shadow AI breach (IBM 2025)' },
    ],
    capabilities: [
      { title: 'Shadow AI Discovery',        desc: 'Find every AI tool employees are using — including the ones nobody approved and nobody knows about.' },
      { title: 'Agent IAM & Governance',     desc: 'Active Directory for AI agents — 17 governance policies out of the box, enforced autonomously.' },
      { title: 'MCP Security',               desc: 'Discover and govern every MCP server your agents connect to before they become a liability.' },
      { title: 'Prompt Injection Detection', desc: 'Catch adversarial attacks in <5ms with pattern matching and an AI judge running in parallel.' },
      { title: 'Compliance Mapping',         desc: 'EU AI Act, NIST AI RMF, SOC 2, HIPAA, ISO 42001 — mapped to your policy posture automatically.' },
    ],
    cta: 'See RevSec in Action →',
    route: '/ai-ciso',
  },
  revmigrate: {
    id: 'revmigrate', name: 'RevMigrate + RevModernize', label: 'AI DBA + AI Architect',
    color: '#F59E0B', rgb: '245,158,11',
    description: "Your data is trapped in legacy and expensive databases. Your applications can't move. Your AI roadmap is waiting on both. RevMigrate and RevModernize are two AI agents built for one job: closing the gap between your legacy infrastructure and the AI-ready foundation your strategy requires. One handles your database migration and modernisation. One handles your application modernisation and AI readiness. Together they handle the transformation program that's been on your roadmap for three years and hasn't started yet.",
    audience: "For CTOs, Data Engineering leads, and IT Directors carrying legacy database and architecture risk",
    stats: [
      { value: '88%', label: 'reduction in migration effort vs traditional approach' },
      { value: '73%', label: 'average cost reduction — ROI in under 8 months' },
    ],
    capabilities: [
      { title: 'AI Scout — Compatibility Intelligence', desc: 'Before a single object moves, AI Scout analyses your full schema across 12+ target databases and shows you exactly what migrates cleanly, what needs work, and what the effort looks like.' },
      { title: 'Digital DBA',                          desc: 'Just tell it what you want to migrate. The Digital DBA handles schema analysis, generates the migration plan, and starts the pipeline — no specialist SQL knowledge required from your team.' },
      { title: 'Automated Migration Agents',           desc: '4 agents run in parallel — Migration Agent converts objects, Dependency Mapper tracks relationships, Health Monitor tracks performance, Query Optimizer tunes for your new stack.' },
      { title: 'AI-Readiness Validation',              desc: 'Your migrated database scored across readiness dimensions before handover. 100/100 means your new stack is ready for AI agents from day one.' },
      { title: 'Agent Integration Layer',              desc: 'We build the API layer, event hooks, and vector stores your agents need — your engineers ship features, not plumbing.' },
    ],
    cta: 'See RevMigrate in Action →',
    route: '/ai-dba',
  },
  revmodernize: {
    id: 'revmodernize', name: 'RevModernize', label: 'AI Architect',
    color: C2, rgb: R2,
    description: "Legacy applications don't block AI adoption by accident — they block it by design. RevModernize identifies exactly what's standing between your current architecture and your AI roadmap, then closes that gap with your team in control at every decision that matters.",
    audience: "For technology leaders who are accountable for AI delivery and cannot afford another transformation program that takes three years to prove it failed",
    stats: [
      { value: '18→91', label: 'AI-readiness score achieved in 8–10 weeks' },
      { value: '87%',   label: 'of AI projects fail before reaching production' },
    ],
    capabilities: [
      { title: 'AI-Readiness Assessment',  desc: 'Score your codebase across 8 dimensions — know exactly what blocks your AI roadmap and what does not.' },
      { title: 'Legacy Code Analysis',     desc: 'Map every dependency, API, and data flow your AI agents will need to reach and trigger.' },
      { title: 'Agent Integration Layer',  desc: 'We build the API layer, event hooks, and vector stores your agents need — your engineers ship features, not plumbing.' },
      { title: 'Incremental delivery',     desc: 'Refactor without rewriting — we ship AI-native value in weeks, not after a multi-year programme.' },
      { title: 'Architectural oversight',  desc: 'Every significant decision reviewed and signed off by a senior RevUpAI architect before it goes anywhere near production.' },
    ],
    cta: 'See RevModernize in Action →',
    route: '/ai-architect',
  },
  revarchitect: {
    id: 'revarchitect', name: 'Custom AI Roles', label: 'AI Implementation',
    color: C2, rgb: R2,
    description: "You've bought the licences, run the pilots, hired the consultants — and 6 months later your AI projects are still stuck between POC and production. RevUpAI cuts through the chaos: a clear path from where you are today to AI roles that are live, operating, and delivering value. No more framework overload. No more pilot purgatory.",
    audience: "For CTOs and Engineering leaders whose AI roadmap is stuck — and whose board is asking why",
    stats: [
      { value: '87%',   label: 'of AI projects fail before reaching production' },
      { value: '6 wks', label: 'from assessment to first AI role in production' },
    ],
    cta: 'Explore Custom AI Roles →',
    route: '/ai-roles',
  },
};

/* ─── Hero problem cards ─────────────────────────────────────────────────────── */
const HERO_PROBLEMS = [
  {
    id: 'shadow',
    label: 'THE BLIND SPOT',
    title: 'Shadow AI & Unmonitored Agents',
    color: '#7C3AED', rgb: '124,58,237',
    roleId: 'revsec',
    roleTag: 'RevSec · AI CISO',
  },
  {
    id: 'legacy',
    label: 'THE ANCHOR',
    title: 'Legacy Systems & Database Lock-in',
    color: '#F59E0B', rgb: '245,158,11',
    roleId: 'revmigrate',
    roleTag: 'RevMigrate + RevModernize',
  },
  {
    id: 'chaos',
    label: 'THE MAZE',
    title: 'AI Implementation Chaos',
    color: '#00D4AA', rgb: '0,212,170',
    roleId: 'revarchitect',
    roleTag: 'RevModernize · AI Architect',
  },
  {
    id: 'custom',
    label: 'THE BUILD',
    title: "Need a role we haven't built yet?",
    color: '#C8C0EC', rgb: '200,192,236',
    roleId: null,
    roleTag: 'Custom AI Role',
    isCustom: true,
  },
];

/* ─── HeroProblemPanel ───────────────────────────────────────────────────────── */
function HeroProblemPanel() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(null);

  const activeProblem = HERO_PROBLEMS.find(p => p.id === activeId);
  const activeRole    = activeProblem && activeProblem.roleId ? ROLES[activeProblem.roleId] : null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:0 }}>

      <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:LAV3, margin:'0 0 10px' }}>
        Which problem is costing you most?
      </p>

      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {HERO_PROBLEMS.map(p => {
          const isActive = p.id === activeId;
          return (
            <button key={p.id}
              onClick={() => setActiveId(isActive ? null : p.id)}
              style={{
                padding:'14px 18px', borderRadius:10, cursor:'pointer', textAlign:'left', border:'none',
                background: isActive ? `rgba(${p.rgb},.1)` : 'rgba(255,255,255,.025)',
                outline: `1px solid ${isActive ? `rgba(${p.rgb},.38)` : 'rgba(255,255,255,.06)'}`,
                transition:'all .18s ease', display:'flex', alignItems:'center', gap:12, width:'100%',
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background=`rgba(${p.rgb},.06)`; e.currentTarget.style.outline=`1px solid rgba(${p.rgb},.2)`; }}}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background='rgba(255,255,255,.025)'; e.currentTarget.style.outline='1px solid rgba(255,255,255,.06)'; }}}
            >
              <div style={{ width:7, height:7, borderRadius:'50%', background:p.color, flexShrink:0, opacity:isActive?1:0.45, boxShadow:isActive?`0 0 8px ${p.color}`:'none' }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:9.5, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:isActive?p.color:LAV3, marginBottom:2 }}>{p.label}</div>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(13px,1.1vw,15px)', fontWeight:700, color:isActive?LAV:LAV2, lineHeight:1.3 }}>{p.title}</div>
              </div>
              <span style={{ fontSize:18, fontWeight:700, color:isActive?p.color:`rgba(${p.rgb},.75)`, transition:'all .2s', transform:isActive?'rotate(90deg)':'none', lineHeight:1, flexShrink:0 }}>›</span>
            </button>
          );
        })}
      </div>

      {activeProblem && (
        <div style={{
          marginTop:8, borderRadius:10,
          border:`1px solid rgba(${activeProblem.rgb},.22)`,
          background:`rgba(${activeProblem.rgb},.05)`,
          overflow:'hidden', animation:'fadeIn .2s ease',
        }}>
          {activeProblem.isCustom ? (
            <div style={{ padding:'18px 20px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <div style={{ width:7, height:7, borderRadius:'50%', background:activeProblem.color }}/>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:700, color:activeProblem.color }}>Custom AI Role</span>
              </div>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:300, color:LAV2, margin:'0 0 14px', lineHeight:1.7 }}>
                We build AI roles for processes unique to your business — claims processing, clinical documentation, contract review, compliance monitoring. Your process, built as AI, operated by us.
              </p>
              <button onClick={() => navigate('/contact')}
                style={{ padding:'10px 20px', borderRadius:9, cursor:'pointer', border:`1px solid rgba(${activeProblem.rgb},.3)`, background:`rgba(${activeProblem.rgb},.08)`, color:activeProblem.color, fontSize:12.5, fontWeight:700, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .18s ease' }}
                onMouseEnter={e=>{ e.currentTarget.style.background=`rgba(${activeProblem.rgb},.16)`; }}
                onMouseLeave={e=>{ e.currentTarget.style.background=`rgba(${activeProblem.rgb},.08)`; }}
              >Tell us about your process →</button>
            </div>
          ) : activeRole ? (
            <div>
              <div style={{ padding:'12px 16px', borderBottom:`1px solid rgba(${activeProblem.rgb},.12)`, display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:activeRole.color }}/>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:700, color:activeRole.color }}>{activeRole.name}</span>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:500, color:LAV3, marginLeft:2 }}>· {activeRole.label}</span>
              </div>
              <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:10 }}>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:300, color:LAV2, margin:0, lineHeight:1.7 }}>{activeRole.description}</p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  {activeRole.stats.map((s,i) => (
                    <div key={i} style={{ padding:'10px 12px', borderRadius:8, background:`rgba(${activeRole.rgb},.07)`, border:`1px solid rgba(${activeRole.rgb},.15)` }}>
                      <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:18, fontWeight:900, color:LAV, lineHeight:1, marginBottom:4 }}>{s.value}</div>
                      <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10.5, fontWeight:400, color:LAV3, lineHeight:1.4 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:400, color:LAV3, margin:0, lineHeight:1.5, display:'flex', alignItems:'flex-start', gap:6 }}>
                  <span style={{ opacity:.7 }}>👤</span>
                  <span>{activeRole.audience}</span>
                </p>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button onClick={() => navigate(activeRole.route)}
                    style={{ padding:'11px 16px', borderRadius:9, cursor:'pointer', background:`rgba(${activeRole.rgb},.12)`, border:`1px solid rgba(${activeRole.rgb},.35)`, color:LAV, fontSize:13, fontWeight:700, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .18s ease' }}
                    onMouseEnter={e=>{ e.currentTarget.style.background=`rgba(${activeRole.rgb},.22)`; e.currentTarget.style.transform='translateY(-1px)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background=`rgba(${activeRole.rgb},.12)`; e.currentTarget.style.transform='none'; }}
                  >{activeRole.cta}</button>
                  <button onClick={() => navigate('/contact')}
                    style={{ padding:'11px 16px', borderRadius:9, cursor:'pointer', background:'transparent', border:'1px solid rgba(255,255,255,.1)', color:LAV3, fontSize:13, fontWeight:500, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .18s ease' }}
                    onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(255,255,255,.25)'; e.currentTarget.style.color=LAV; }}
                    onMouseLeave={e=>{ e.currentTarget.style.borderColor='rgba(255,255,255,.1)'; e.currentTarget.style.color=LAV3; }}
                  >Book a Discovery Call →</button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      <div style={{ marginTop:10, padding:'9px 14px', borderRadius:9, background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.06)', display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:9.5, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:LAV3, flexShrink:0 }}>Coming soon</span>
        <div style={{ width:1, height:12, background:'rgba(255,255,255,.1)', flexShrink:0 }}/>
        <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, color:LAV3, opacity:.55 }}>AI QA</span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   HERO SECTION — v8 mobile responsive
   ════════════════════════════════════════════════════════════════════════════ */
export function HeroSection({ onSeeDemo }) {
  const navigate  = useNavigate();
  const mob       = useIsMobile();

  const stats = [
    { value:'89%',    label:'Shadow AI found in 48 hours', color:C2 },
    { value:'$1.88M', label:'Annual saving per migration',  color:'#F59E0B' },
    { value:'97%',    label:'Policy compliance achieved',   color:C },
  ];

  return (
    <section style={{
      background: BG,
      display: 'flex',
      alignItems: 'flex-start',
      padding: mob
        ? '88px 0 80px'
        : 'clamp(88px,8vh,110px) 0 clamp(80px,10vh,120px)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div aria-hidden style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
        <div style={{ position:'absolute', top:'-10%', left:'-5%', width:'60%', height:'80%', borderRadius:'50%', background:`radial-gradient(ellipse,rgba(${R},.14) 0%,transparent 65%)`, filter:'blur(80px)' }}/>
        <div style={{ position:'absolute', bottom:'-10%', right:'-5%', width:'50%', height:'70%', borderRadius:'50%', background:`radial-gradient(ellipse,rgba(${R2},.08) 0%,transparent 65%)`, filter:'blur(100px)' }}/>
        <div style={{ position:'absolute', inset:0, backgroundImage:`linear-gradient(rgba(237,233,248,.012) 1px,transparent 1px),linear-gradient(90deg,rgba(237,233,248,.012) 1px,transparent 1px)`, backgroundSize:'80px 80px', maskImage:'radial-gradient(ellipse 80% 80% at 50% 50%,black 40%,transparent 100%)' }}/>
      </div>

      <div style={{ ...WRAP, position:'relative', zIndex:1 }}>
        <div style={{
          display:'grid',
          gridTemplateColumns: mob ? '1fr' : '1.1fr 0.9fr',
          gap: mob ? '48px' : 'clamp(48px,5vw,88px)',
          alignItems:'center',
        }}>

          {/* ── LEFT ── */}
          <div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px 5px 10px', borderRadius:100, marginBottom:24, background:`rgba(${R2},0.08)`, border:`1px solid rgba(${R2},0.22)` }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:C2, animation:'pulse 2s ease-in-out infinite' }}/>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:`rgba(${R2},.8)` }}>Operated AI · Roles rebuilt as AI agents</span>
            </div>

            <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize: mob ? '42px' : 'clamp(32px,3.8vw,60px)', lineHeight:1.06, letterSpacing:'-2px', color:LAV, margin:'0 0 8px' }}>
              Enterprise roles,
            </h1>
            <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize: mob ? '42px' : 'clamp(32px,3.8vw,60px)', lineHeight:1.06, letterSpacing:'-2px', color:C2, margin:'0 0 24px' }}>
              reimagined as AI.
            </h1>

            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize: mob ? '16px' : 'clamp(15px,1.15vw,18px)', fontWeight:300, lineHeight:1.8, color:LAV3, margin:'0 0 14px' }}>
              Every enterprise needs a CISO, a DBA, and an Architect. We've built them as{' '}
              <span style={{ color:LAV2, fontWeight:500 }}>Operated AI roles</span> — not tools you configure, but roles that work.
            </p>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize: mob ? '16px' : 'clamp(15px,1.15vw,18px)', fontWeight:300, lineHeight:1.8, color:LAV3, margin:'0 0 14px' }}>
              Each role ships with our domain experts{' '}
              <span style={{ color:LAV2, fontWeight:500 }}>handling every human-in-the-loop interaction</span> — so the complexity never lands on your team. Full auditability, zero burden.
            </p>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize: mob ? '16px' : 'clamp(15px,1.15vw,18px)', fontWeight:400, lineHeight:1.8, color:LAV2, margin:'0 0 32px' }}>
              Not a toolkit. Not a pilot.{' '}
              <span style={{ color:LAV, fontWeight:500 }}>A role that does the job from day one.</span>
            </p>

            <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:40 }}>
              <button onClick={()=>navigate('/contact')}
                style={{ padding:'14px 26px', borderRadius:10, cursor:'pointer', background:C, border:'none', color:LAV, fontSize:14, fontWeight:700, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:`0 6px 28px rgba(${R},.4)`, transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
                onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 12px 40px rgba(${R},.55)`; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow=`0 6px 28px rgba(${R},.4)`; }}
              >Book a Discovery Call →</button>
              <button onClick={onSeeDemo}
                style={{ padding:'14px 26px', borderRadius:10, cursor:'pointer', background:'rgba(237,233,248,.04)', border:`1px solid ${C2}`, color:LAV2, fontSize:14, fontWeight:500, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
                onMouseEnter={e=>{ e.currentTarget.style.background='rgba(237,233,248,.07)'; e.currentTarget.style.borderColor=`rgba(${R2},.45)`; }}
                onMouseLeave={e=>{ e.currentTarget.style.background='rgba(237,233,248,.04)'; e.currentTarget.style.borderColor=`rgba(${R2},.25)`; }}
              >See a role in action ↓</button>
            </div>

            <div style={{ display:'flex', borderTop:`1px solid rgba(${R2},.1)`, paddingTop:24, gap:0 }}>
              {stats.map((s,i) => (
                <div key={i} style={{ flex:1, paddingRight:i<stats.length-1?16:0, borderRight:i<stats.length-1?`1px solid rgba(${R2},.1)`:'none', marginRight:i<stats.length-1?16:0 }}>
                  <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize: mob ? '22px' : 'clamp(20px,1.8vw,28px)', fontWeight:900, color:s.color, lineHeight:1, marginBottom:6 }}>{s.value}</div>
                  <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize: mob ? '10px' : '11px', fontWeight:400, color:LAV3, lineHeight:1.45 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT — problem selector ── */}
          <div>
            <HeroProblemPanel/>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   PROBLEM SECTION
   ════════════════════════════════════════════════════════════════════════════ */
export function ProblemSection() {
  const problems = [
    {
      label:'THE BLIND SPOT', heading:'Shadow AI & Unmonitored Agents',
      color:'#7C3AED', rgb:'124,58,237',
      stat:'67%', statLabel:"of employees use AI tools IT never approved — and that's before you count autonomous agents",
      points:[
        'Sensitive data flowing to unknown LLMs — no visibility, no control, no audit trail',
        'AI agents are making decisions using your business data. But who is watching the AI agents?',
        'No compliance posture for EU AI Act, SOC2, or HIPAA — one audit away from a crisis',
      ],
      consequence:'One breach. One lawsuit. Game over.',
    },
    {
      label:'THE ANCHOR', heading:'Legacy Systems Lock You Out of AI',
      color:'#F59E0B', rgb:'245,158,11',
      stat:'70%', statLabel:"of IT budgets trapped in systems that can't connect to a single AI agent or model",
      points:[
        'Oracle, COBOL, PowerBuilder — your AI agents cannot reach them, trigger them, or learn from them',
        'Modernising old code is not the same as becoming AI-ready. Your competitors know the difference.',
        'By the time a traditional migration finishes, the AI landscape has moved on twice over',
      ],
      consequence:"By the time you're ready, the market has moved on.",
    },
    {
      label:'THE MAZE', heading:'AI Implementation Chaos',
      color:'#00D4AA', rgb:'0,212,170',
      stat:'87%', statLabel:'of AI projects never reach production — they die somewhere between pilot and real value',
      points:[
        'Framework overload with no enterprise AI operations playbook to follow',
        'Pilot programmes that consume engineering budgets and ship nothing to production',
        'You need AI roles working in your organisation. Not another proof of concept.',
      ],
      consequence:"Experiments don't create enterprise value.",
    },
  ];

  return (
    <section style={{ background:'#0D0A17', padding:`${SECTION_PAD} 0`, borderTop:'1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ ...WRAP }}>
        <div style={{ marginBottom:'clamp(48px,6vh,72px)' }}>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:LAV3, margin:'0 0 16px' }}>The Problems We Solve</p>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(28px,3.5vw,52px)', lineHeight:1.1, letterSpacing:'-1.5px', color:LAV, margin:0 }}>
            Your enterprise has three problems.<br/>
            <span style={{ color:C2, fontWeight:400, fontStyle:'italic' }}>Most vendors solve none of them.</span>
          </h2>
        </div>
        {/* auto-fit — already wraps on mobile */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:20 }}>
          {problems.map((p,i)=>(
            <div key={i}
              style={{ borderRadius:16, background:'rgba(255,255,255,0.025)', border:`1px solid rgba(${p.rgb},0.15)`, overflow:'hidden', display:'flex', flexDirection:'column', transition:'all .25s ease' }}
              onMouseEnter={e=>{ e.currentTarget.style.background=`rgba(${p.rgb},0.05)`; e.currentTarget.style.borderColor=`rgba(${p.rgb},0.3)`; e.currentTarget.style.transform='translateY(-3px)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.025)'; e.currentTarget.style.borderColor=`rgba(${p.rgb},0.15)`; e.currentTarget.style.transform='none'; }}
            >
              <div style={{ padding:'22px 24px', borderBottom:`1px solid rgba(${p.rgb},0.12)`, display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                <div>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:800, letterSpacing:2.5, textTransform:'uppercase', color:LAV3, margin:'0 0 8px' }}>{p.label}</p>
                  <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(18px,1.8vw,24px)', fontWeight:800, color:LAV, margin:0, lineHeight:1.2 }}>{p.heading}</h3>
                </div>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(28px,3vw,42px)', fontWeight:900, color:p.color, lineHeight:1, flexShrink:0 }}>{p.stat}</div>
              </div>
              <div style={{ padding:'10px 24px', background:`rgba(${p.rgb},0.06)`, borderBottom:`1px solid rgba(${p.rgb},0.08)` }}>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, color:LAV2, margin:0, fontWeight:400, lineHeight:1.5, letterSpacing:.2 }}>{p.statLabel}</p>
              </div>
              <div style={{ padding:'20px 24px', flex:1, display:'flex', flexDirection:'column', gap:14 }}>
                {p.points.map((pt,j)=>(
                  <div key={j} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                    <div style={{ width:5, height:5, borderRadius:'50%', background:p.color, opacity:.5, marginTop:7, flexShrink:0 }}/>
                    <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, lineHeight:1.7, color:LAV2, fontWeight:300 }}>{pt}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding:'16px 24px', borderTop:`1px solid rgba(${p.rgb},0.1)`, background:`rgba(${p.rgb},0.04)` }}>
                <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:15, fontWeight:700, color:p.color, margin:0 }}>{p.consequence}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   CONTRAST SECTION — mobile: 2-col (label + RevUpAI only)
   ════════════════════════════════════════════════════════════════════════════ */
export function ContrastSection() {
  const mob = useIsMobile();
  const rows = [
    { label:'What you get',     a:'A platform to build on',          b:'Productivity assistance',          c:'A deployed AI role — ready to work' },
    { label:'Who operates it',  a:'Your team figures it out',        b:'The vendor, within their app',     c:'RevUpAI operates it in your environment' },
    { label:'Time to value',    a:'Months of engineering to build',  b:'Immediate, but limited in scope',  c:'Weeks to deploy, working from day one' },
    { label:'Human oversight',  a:'You design and configure it',     b:'Limited to assisted tasks',        c:'Built-in HITL at every risk decision' },
    { label:'Domain expertise', a:'You provide all of it',           b:'General-purpose assistance',       c:'Domain specialists in the loop always' },
  ];
  return (
    <section style={{ background:BG, padding:`${SECTION_PAD} 0`, borderTop:'1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ ...WRAP }}>
        <div style={{ marginBottom:56 }}>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:`rgba(${R2},.7)`, margin:'0 0 16px' }}>Why RevUpAI</p>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(28px,3.5vw,52px)', lineHeight:1.1, letterSpacing:'-1.5px', color:LAV, margin:'0 0 20px', maxWidth:720 }}>
            Most vendors give you a platform to build agents.{' '}
            <span style={{ color:C2 }}>We give you the role that does the job.</span>
          </h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.1vw,17px)', fontWeight:300, lineHeight:1.8, color:LAV3, maxWidth:580 }}>
            Platform vendors hand you a toolkit and leave. We deploy a specialist AI role into your environment, operate it for you, and keep domain experts in the loop at every decision that carries real risk.
          </p>
        </div>

        {mob ? (
          /* ── Mobile: stacked cards, RevUpAI value only ── */
          <div style={{ display:'flex', flexDirection:'column', gap:1, borderRadius:14, overflow:'hidden', border:'1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ padding:'14px 18px', background:`rgba(${R2},0.06)`, borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:C2 }}/>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:700, color:LAV }}>RevUpAI · Operated AI roles</span>
            </div>
            {rows.map((row,i) => (
              <div key={i} style={{ padding:'14px 18px', borderBottom:i<rows.length-1?'1px solid rgba(255,255,255,0.05)':'none', background:i%2===0?'rgba(255,255,255,0.015)':'transparent', display:'flex', justifyContent:'space-between', alignItems:'center', gap:16 }}>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, color:'rgba(200,192,236,0.5)', textTransform:'uppercase', letterSpacing:1, flexShrink:0 }}>{row.label}</span>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:600, color:LAV, lineHeight:1.5, textAlign:'right' }}>{row.c}</span>
              </div>
            ))}
          </div>
        ) : (
          /* ── Desktop: full 4-col table ── */
          <div style={{ borderRadius:16, overflow:'hidden', border:'1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ display:'grid', gridTemplateColumns:'190px 1fr 1fr 1fr', background:'rgba(255,255,255,0.04)', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ padding:'16px 20px' }}/>
              {[
                { label:'Platform vendors',   sub:'Build it yourself',    highlight:false },
                { label:'Copilot assistants', sub:'Assisted productivity', highlight:false },
                { label:'RevUpAI',            sub:'Operated AI roles',    highlight:true  },
              ].map((col,i)=>(
                <div key={i} style={{ padding:'16px 20px', borderLeft:'1px solid rgba(255,255,255,0.06)', background:col.highlight?`rgba(${R2},0.05)`:'transparent' }}>
                  <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:700, color:col.highlight?LAV:LAV2, marginBottom:4 }}>{col.label}</div>
                  <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, color:col.highlight?`rgba(${R2},.7)`:'rgba(200,192,236,0.35)', fontWeight:col.highlight?600:400 }}>{col.sub}</div>
                </div>
              ))}
            </div>
            {rows.map((row,i)=>(
              <div key={i} style={{ display:'grid', gridTemplateColumns:'190px 1fr 1fr 1fr', borderBottom:i<rows.length-1?'1px solid rgba(255,255,255,0.05)':'none', background:i%2===0?'rgba(255,255,255,0.015)':'transparent' }}>
                <div style={{ padding:'16px 20px', display:'flex', alignItems:'center' }}>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, color:'rgba(200,192,236,0.5)', textTransform:'uppercase', letterSpacing:1 }}>{row.label}</span>
                </div>
                <div style={{ padding:'16px 20px', borderLeft:'1px solid rgba(255,255,255,0.05)', display:'flex', alignItems:'center' }}>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, color:'rgba(200,192,236,0.45)', lineHeight:1.5 }}>{row.a}</span>
                </div>
                <div style={{ padding:'16px 20px', borderLeft:'1px solid rgba(255,255,255,0.05)', display:'flex', alignItems:'center' }}>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, color:'rgba(200,192,236,0.45)', lineHeight:1.5 }}>{row.b}</span>
                </div>
                <div style={{ padding:'16px 20px', borderLeft:`1px solid rgba(${R2},0.15)`, display:'flex', alignItems:'center', background:`rgba(${R2},0.04)` }}>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:600, color:LAV, lineHeight:1.5 }}>{row.c}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop:28, padding:'20px 28px', background:`rgba(${R2},.06)`, border:`1px solid rgba(${R2},.18)`, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'space-between', gap:20, flexWrap:'wrap' }}>
          <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:'clamp(14px,1.2vw,18px)', fontWeight:700, color:LAV, margin:0 }}>
            "They sell platforms. We deploy roles that do the job — and we operate them for you."
          </p>
          <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:C2 }}/>
            <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, color:`rgba(${R2},.7)`, letterSpacing:2, textTransform:'uppercase' }}>Operated AI</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   HITL / HOW IT WORKS — stacks on mobile
   ════════════════════════════════════════════════════════════════════════════ */
export function HITLSection() {
  const mob = useIsMobile();
  const steps = [
    { n:'01', heading:'We deploy the role', sub:'Into your environment', color:C2, rgb:R2,
      body:'We take a production-ready AI role — RevSec, RevMigrate, RevModernize, or a custom role built for your specific process — and deploy it inside your infrastructure. Your data stays in your environment. Weeks, not months.' },
    { n:'02', heading:'The role operates 24/7', sub:'We manage it — not you', color:C2, rgb:R2,
      body:"The AI role runs continuously — scanning, analysing, building, monitoring. You don't manage it. We do. Our team handles failures, improves performance, and keeps it running. That's Operated AI." },
    { n:'03', heading:'Critical decisions escalate', sub:'Experts handle every risk decision', color:'#00D4AA', rgb:'0,212,170',
      body:"Routine work is automated. Decisions that carry real risk — regulatory, architectural, legal — surface to a RevUpAI domain specialist. Not a generic approver. An expert who understands exactly what's at stake." },
  ];
  return (
    <section style={{ background:'#0D0A17', padding:`${SECTION_PAD} 0`, borderTop:'1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ ...WRAP }}>
        <div style={{
          display:'grid',
          gridTemplateColumns: mob ? '1fr' : '1fr 1fr',
          gap: mob ? '48px' : 'clamp(60px,7vw,120px)',
          alignItems:'center',
        }}>
          <div>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:LAV3, margin:'0 0 16px' }}>How It Works</p>
            <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(28px,3vw,46px)', lineHeight:1.1, letterSpacing:'-1.5px', color:LAV, margin:'0 0 22px' }}>
              Deploy. Operate.<br/>
              <span style={{ color:C2, fontWeight:400, fontStyle:'italic' }}>Experts sign off on every critical decision.</span>
            </h2>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.1vw,17px)', fontWeight:300, lineHeight:1.8, color:LAV3, margin:'0 0 18px' }}>
              Every AI role follows the same operating model. The AI handles volume, speed, and consistency. Domain experts handle decisions that carry real business or regulatory risk. We operate all of it.
            </p>
            <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:15, fontWeight:300, color:C2, margin:0 }}>
              "Agent + Domain Expertise + Operation = Outcome. All three. Always."
            </p>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginTop:28, padding:'6px 16px', borderRadius:100, background:`rgba(${R2},.08)`, border:`1px solid rgba(${R2},.2)` }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:C2 }}/>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:C2 }}>Operated AI — not a tool you manage</span>
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column' }}>
            {steps.map((s,i)=>(
              <div key={i} style={{ display:'flex', gap:20, padding:'28px 0', borderBottom:i<steps.length-1?'1px solid rgba(255,255,255,0.06)':'none' }}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                  <div style={{ width:44, height:44, borderRadius:12, background:`rgba(${s.rgb},.1)`, border:`1px solid rgba(${s.rgb},.25)`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <span style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:14, fontWeight:800, color:s.color }}>{s.n}</span>
                  </div>
                  {i<steps.length-1&&<div style={{ width:1, flex:1, background:`rgba(${R2},.15)`, margin:'8px 0' }}/>}
                </div>
                <div style={{ paddingTop:4 }}>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:LAV3, margin:'0 0 6px' }}>{s.sub}</p>
                  <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(17px,1.5vw,22px)', fontWeight:800, color:LAV, margin:'0 0 10px', lineHeight:1.25 }}>{s.heading}</h3>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.75, color:LAV3, margin:0 }}>{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   STATS BAND — 5-col desktop / 2-col+1 mobile
   ════════════════════════════════════════════════════════════════════════════ */
export function StatsBand() {
  const mob = useIsMobile();
  const stats = [
    { value:'$1.88M', label:'Annual saving per RevMigrate deployment',  color:C2,        rgb:R2 },
    { value:'89%',    label:'Shadow AI discovered within 48 hours',      color:C2,        rgb:R2 },
    { value:'38 min', label:'Full regression suite vs. 3.5-week cycle',  color:C,         rgb:R },
    { value:'8 wks',  label:'Oracle to PostgreSQL — not 18 months',      color:'#F59E0B', rgb:'245,158,11' },
    { value:'97%',    label:'Policy compliance achieved post-deployment', color:C,         rgb:R },
  ];
  return (
    <section style={{ background:`rgba(${R},.04)`, borderTop:`1px solid rgba(${R},.1)`, borderBottom:`1px solid rgba(${R},.08)`, padding:`clamp(40px,5vh,60px) 0` }}>
      <div style={{ ...WRAP }}>
        <div style={{
          display:'grid',
          gridTemplateColumns: mob ? 'repeat(2,1fr)' : 'repeat(5,1fr)',
          gap: mob ? '1px' : '0',
        }}>
          {stats.map((s,i)=>(
            <div key={i} style={{
              padding: mob ? '24px 16px' : '28px 20px',
              borderRight: mob ? 'none' : (i<stats.length-1?'1px solid rgba(237,233,248,0.05)':'none'),
              borderBottom: mob ? (i<4?'1px solid rgba(237,233,248,0.05)':'none') : 'none',
              textAlign:'center',
              // last item on mobile spans full width
              gridColumn: mob && i===4 ? 'span 2' : 'span 1',
            }}>
              <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize: mob ? 'clamp(28px,8vw,36px)' : 'clamp(24px,2.5vw,40px)', fontWeight:900, color:s.color, lineHeight:1, marginBottom:10 }}>{s.value}</div>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:400, lineHeight:1.5, color:LAV3, margin:0 }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   DUAL CTA — stacks on mobile
   ════════════════════════════════════════════════════════════════════════════ */
export function DualCTA() {
  const navigate = useNavigate();
  const mob      = useIsMobile();
  return (
    <section style={{ background:`linear-gradient(135deg,rgba(${R},.1) 0%,rgba(${R2},.06) 60%,${BG} 100%)`, borderTop:`1px solid rgba(${R},.1)`, padding:`${SECTION_PAD} 0`, position:'relative', overflow:'hidden' }}>
      <div aria-hidden style={{ position:'absolute', top:'-20%', left:'50%', transform:'translateX(-50%)', width:800, height:800, borderRadius:'50%', background:`radial-gradient(circle,rgba(${R},.12) 0%,transparent 65%)`, filter:'blur(100px)', pointerEvents:'none' }}/>
      <div style={{ ...WRAP, position:'relative', zIndex:1 }}>
        <div style={{ textAlign:'center', marginBottom:64 }}>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:`rgba(${R2},.7)`, margin:'0 0 16px' }}>Work With Us</p>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(28px,3.5vw,52px)', lineHeight:1.1, letterSpacing:'-1.5px', color:LAV, margin:'0 auto 16px', maxWidth:680 }}>
            Ready to deploy an AI role —{' '}
            <span style={{ color:C2 }}>not another pilot?</span>
          </h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.1vw,17px)', fontWeight:300, lineHeight:1.8, color:LAV3, margin:'0 auto', maxWidth:540 }}>
            Start with one of our production-ready AI roles — or work with us to build a custom AI role for a process unique to your business.
          </p>
        </div>
        <div style={{ display:'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap:20, maxWidth:900, margin:'0 auto 28px' }}>
          <div style={{ padding: mob ? '28px 24px' : '40px 36px', background:`rgba(${R2},.06)`, border:`1px solid rgba(${R2},.2)`, borderRadius:14, display:'flex', flexDirection:'column' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 12px', borderRadius:100, background:`rgba(${R2},.1)`, border:`1px solid rgba(${R2},.25)`, marginBottom:20, alignSelf:'flex-start' }}>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${R2},.8)` }}>For CISOs &amp; CTOs</span>
            </div>
            <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(18px,1.8vw,26px)', fontWeight:800, color:LAV, margin:'0 0 14px', lineHeight:1.25 }}>Get a free AI risk scan</h3>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.75, color:LAV3, margin:'0 0 20px', flex:1 }}>
              Connect RevSec to your environment. See every AI tool and agent — approved, shadow, and unknown — within 48 hours. No commitment. A real picture of your actual exposure.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:28 }}>
              {['Full AI and agent inventory within 48 hours','Risk scores for every tool and agent discovered','Board-ready summary — yours to keep, no strings attached'].map((pt,i)=>(
                <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                  <div style={{ width:5, height:5, borderRadius:'50%', background:C2, flexShrink:0, marginTop:6 }}/>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:LAV2, fontWeight:400, lineHeight:1.5 }}>{pt}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>navigate('/contact')}
              style={{ padding:'15px 28px', borderRadius:10, cursor:'pointer', background:C2, color:'#09060F', border:'none', fontSize:14, fontWeight:800, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:`0 6px 24px rgba(${R2},.35)`, transition:'all .22s ease', width:'100%' }}
              onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 10px 32px rgba(${R2},.5)`; }}
              onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow=`0 6px 24px rgba(${R2},.35)`; }}
            >Get My Free AI Risk Scan →</button>
          </div>
          <div style={{ padding: mob ? '28px 24px' : '40px 36px', background:`rgba(${R},.06)`, border:`1px solid rgba(${R},.2)`, borderRadius:14, display:'flex', flexDirection:'column' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 12px', borderRadius:100, background:`rgba(${R},.1)`, border:`1px solid rgba(${R},.25)`, marginBottom:20, alignSelf:'flex-start' }}>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${R},.8)` }}>For CEOs, COOs &amp; CFOs</span>
            </div>
            <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(18px,1.8vw,26px)', fontWeight:800, color:LAV, margin:'0 0 14px', lineHeight:1.25 }}>Book a Discovery Call</h3>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.75, color:LAV3, margin:'0 0 20px', flex:1 }}>
              30 minutes. We'll map your AI exposure, your AI-readiness gap, and which role — or custom build — makes sense first. You leave with a timeline and business case for the board.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:28 }}>
              {['30-minute session with a RevUpAI specialist','AI-readiness gap analysis for your stack','Business case with ROI numbers — yours to keep'].map((pt,i)=>(
                <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                  <div style={{ width:5, height:5, borderRadius:'50%', background:C, flexShrink:0, marginTop:6 }}/>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:LAV2, fontWeight:400, lineHeight:1.5 }}>{pt}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>navigate('/contact')}
              style={{ padding:'15px 28px', borderRadius:10, cursor:'pointer', background:C, color:LAV, border:'none', fontSize:14, fontWeight:800, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:`0 6px 24px rgba(${R},.35)`, transition:'all .22s ease', width:'100%' }}
              onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 10px 32px rgba(${R},.5)`; }}
              onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow=`0 6px 24px rgba(${R},.35)`; }}
            >Book a Discovery Call →</button>
          </div>
        </div>
        <div style={{ padding: mob ? '20px 20px' : '22px 32px', background:'rgba(0,212,170,.04)', border:'1px solid rgba(0,212,170,.18)', borderRadius:12, maxWidth:900, margin:'0 auto', display:'flex', alignItems: mob ? 'flex-start' : 'center', justifyContent:'space-between', gap:24, flexDirection: mob ? 'column' : 'row', flexWrap:'wrap' }}>
          <div>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:700, color:LAV, margin:'0 0 4px' }}>Need something bespoke?</p>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:LAV3, margin:0, lineHeight:1.6, maxWidth:560 }}>
              We build custom AI roles for processes unique to your business — claims processing, clinical documentation, contract review, compliance monitoring. Your process, built as AI, operated by us.
            </p>
          </div>
          <button onClick={()=>navigate('/contact')}
            style={{ padding:'10px 22px', borderRadius:9, cursor:'pointer', background:'transparent', border:'1px solid rgba(0,212,170,.25)', color:C2, fontSize:13, fontWeight:600, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .2s ease', flexShrink:0, whiteSpace: mob ? 'normal' : 'nowrap', width: mob ? '100%' : 'auto' }}
            onMouseEnter={e=>{ e.currentTarget.style.background='rgba(0,212,170,.07)'; e.currentTarget.style.borderColor='rgba(0,212,170,.4)'; }}
            onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='rgba(0,212,170,.25)'; }}
          >Tell us about your process →</button>
        </div>
      </div>
    </section>
  );
}